import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  ClipboardList,
  FileText,
  Gauge,
  Network,
  RefreshCw,
  ShieldCheck,
  Ship,
} from 'lucide-react';
import type {
  LogisticsCaseCockpitView,
  LogisticsCaseProjection,
  LogisticsNetworkMenuSet,
  LogisticsNetworkR4ToR6RoadmapPack,
  LogisticsR4PilotPrepPack,
  LogisticsProductizationPack,
  LogisticsR2HardeningReview,
  LogisticsR3ReadinessPack,
  LogisticsR3UatPack,
} from '@boox/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type CaseListItem = {
  caseId: string;
  mode: 'plan' | 'execute' | 'closing';
  patterns: string[];
  route: { mode?: 'sea' | 'air' | 'truck' | 'rail' | 'multimodal'; origin?: string; destination?: string; pol?: string; pod?: string };
  riskCount: number;
  approvalRequired: boolean;
};

type CapabilityPayload = {
  capabilityId: string;
  status: LogisticsR3ReadinessPack['status'];
  productizationPack: LogisticsProductizationPack;
  readiness: LogisticsR3ReadinessPack;
  uatPack?: LogisticsR3UatPack;
  roadmapPack?: LogisticsNetworkR4ToR6RoadmapPack;
  pilotPrepPack?: LogisticsR4PilotPrepPack;
  menuSet?: LogisticsNetworkMenuSet;
};

type CasesPayload = {
  items: CaseListItem[];
  total: number;
};

type CockpitPayload = {
  projection: LogisticsCaseProjection;
  cockpitView: LogisticsCaseCockpitView;
  hardeningReview?: LogisticsR2HardeningReview;
  readiness: LogisticsR3ReadinessPack;
  uatPack?: LogisticsR3UatPack;
  roadmapPack?: LogisticsNetworkR4ToR6RoadmapPack;
  pilotPrepPack?: LogisticsR4PilotPrepPack;
  menuSet?: LogisticsNetworkMenuSet;
};

const demoReadiness: LogisticsR3ReadinessPack = {
  capabilityId: 'boox.logistics-network.freight-pattern',
  status: 'R3_UAT_READY',
  gates: [
    {
      gateId: 'contracts.regression',
      label: 'Contracts regression',
      status: 'pass',
      evidence: '@boox/contracts target tests passed.',
    },
    {
      gateId: 'external.live-integrations',
      label: 'Live external integrations',
      status: 'not_applicable',
      evidence: 'External send, booking, award, ledger post and payment movement remain blocked.',
    },
  ],
  productionLimits: [
    'Live email send remains disabled.',
    'Carrier booking remains disabled.',
    'Ledger posting remains disabled.',
  ],
  nextRequiredIntegrations: [
    'Connect approval outbox.',
    'Persist case/profile/action/audit records.',
    'Run full CI gate before enabling live integrations.',
  ],
};

const demoUatPack: LogisticsR3UatPack = {
  capabilityId: 'boox.logistics-network.freight-pattern',
  status: 'R3_UAT_READY',
  readinessStatus: 'R3_UAT_READY',
  scenarios: [
    {
      scenarioId: 'uat.plan.nomination-dg',
      label: 'Plan mode nomination + DG document readiness',
      role: 'internal_ops',
      mode: 'plan',
      caseId: 'FN-R2-NOM-DG-001',
      expectedEvidence: ['attention card visible', 'external send blocked', 'approval fallback visible'],
      status: 'ready',
    },
    {
      scenarioId: 'uat.execute.transhipment',
      label: 'Execute mode transhipment handoff',
      role: 'internal_ops',
      mode: 'execute',
      caseId: 'FN-R2-TRANS-001',
      expectedEvidence: ['execute case visible', 'handoff lens visible', 'booking side effect locked'],
      status: 'ready',
    },
    {
      scenarioId: 'uat.closing.settlement',
      label: 'Closing mode settlement control',
      role: 'internal_finance',
      mode: 'closing',
      caseId: 'FN-R2-CLOSE-001',
      expectedEvidence: ['finance fallback visible', 'ledger posting locked', 'production locks visible'],
      status: 'ready',
    },
  ],
  rehearsalScript: ['Open demo route', 'Review Today Board', 'Open nomination/DG case', 'Check R2 guardrails', 'Confirm production locks'],
  signoffCriteria: ['User can identify where the case is', 'User can identify pending and done work', 'External and financial side effects remain locked'],
  productionLocks: [
    'live email sending remains locked',
    'live carrier or co-loader booking remains locked',
    'live ledger posting remains locked',
    'payment movement remains locked',
  ],
};

const demoRoadmapPack: LogisticsNetworkR4ToR6RoadmapPack = {
  capabilityId: 'boox.logistics-network.freight-pattern',
  status: 'R4_R6_PRD_READY',
  currentRuntimeStatus: 'R3_UAT_READY',
  stages: [
    {
      stageId: 'R4_PILOT_LIVE',
      label: 'R4 Real Tenant Pilot',
      status: 'PRD_READY',
      objective: 'Run one controlled live tenant pilot with approval-only side effects.',
      requiredCapabilities: ['Tenant DB persistence', 'Log DB writer', 'approval outbox'],
      acceptanceCriteria: ['real tenant UAT signs off', 'external side effects require approval', 'rollback tested'],
      productionBoundary: 'real tenant data allowed, external execution remains controlled',
    },
    {
      stageId: 'R5_COMMERCIAL_CAPABILITY',
      label: 'R5 Commercial Capability',
      status: 'PRD_READY',
      objective: 'Package as reusable multi-tenant Boox module.',
      requiredCapabilities: ['pricing pack', 'usage metering', 'second-customer onboarding'],
      acceptanceCriteria: ['second tenant enabled without custom code', 'FM/RM/AC/AI contracts stable'],
      productionBoundary: 'commercial repeatability, not marketplace liquidity',
    },
    {
      stageId: 'R6_NETWORK_ECOSYSTEM',
      label: 'R6 Network Ecosystem',
      status: 'PRD_READY',
      objective: 'Build partner/lane marketplace and network learning moat.',
      requiredCapabilities: ['partner SLA profile', 'network data contract', 'partner API/MCP'],
      acceptanceCriteria: ['partner privacy boundary enforced', 'network fee drafts governed'],
      productionBoundary: 'not production until partner governance and settlement are live',
    },
  ],
  commercialNarrative: ['R4 proves pilot', 'R5 proves repeatability', 'R6 proves network moat'],
  moatNarrative: ['Lego-like business components', 'Business Data Contract', 'network operating graph'],
  nonClaims: ['R6 network marketplace is not complete.', 'This pack makes R4-R6 PRD/spec ready, not production finished.'],
};

const demoPilotPrepPack: LogisticsR4PilotPrepPack = {
  capabilityId: 'boox.logistics-network.freight-pattern',
  status: 'R4_PILOT_PREP_READY',
  roadmapStatus: 'R4_R6_PRD_READY',
  workstreams: [
    {
      workstreamId: 'tenant_scope',
      label: 'Pilot tenant and case scope',
      status: 'ready_to_start',
      requiredBeforeLive: true,
      owner: 'product_ops',
      checklist: ['select tenant', 'select real cases', 'confirm UAT owner'],
    },
    {
      workstreamId: 'persistence',
      label: 'Tenant DB persistence',
      status: 'ready_to_start',
      requiredBeforeLive: true,
      owner: 'platform_engineering',
      checklist: ['persist case state', 'persist profile binding', 'persist UAT evidence'],
    },
    {
      workstreamId: 'approval_outbox',
      label: 'Approval outbox',
      status: 'ready_to_start',
      requiredBeforeLive: true,
      owner: 'workflow_engineering',
      checklist: ['create external approval drafts', 'create finance approval drafts', 'block execution without approval'],
    },
    {
      workstreamId: 'audit_writer',
      label: 'Log DB DebugRecord writer',
      status: 'ready_to_start',
      requiredBeforeLive: true,
      owner: 'observability_engineering',
      checklist: ['write guard decisions', 'propagate run_id', 'attach evidence refs'],
    },
    {
      workstreamId: 'rollback',
      label: 'Pilot rollback drill',
      status: 'ready_to_start',
      requiredBeforeLive: true,
      owner: 'release_owner',
      checklist: ['disable capability flag', 'restore profile binding', 'keep drafts reviewable'],
    },
  ],
  pilotStartChecklist: ['R3 UAT accepted', 'pilot tenant selected', 'persistence reviewed', 'approval outbox enabled', 'rollback drill completed'],
  pilotExitCriteria: ['pilot users operate without support intervention', 'no side effect without approval record', 'business owner signs off R5 packaging'],
  lockedActions: ['live email send remains locked until approval executor is connected', 'payment movement remains locked throughout R4 prep'],
  nonClaims: ['R4 live pilot is not complete.', 'This is pilot preparation readiness only.'],
};

const demoMenuSet: LogisticsNetworkMenuSet = {
  menuSetId: 'boox.menu.logistics-network-capability',
  label: 'Logistics Network Capability Menu Set',
  strategy: 'attach_to_existing_network_menu',
  recommendation:
    'Attach under the existing Network menu first, with tenant-enabled cross-links from FM, AI and Finance instead of creating a disconnected product silo.',
  items: [
    {
      itemId: 'logistics.network.cockpit',
      label: 'Capability Cockpit',
      route: '/network/logistics-capability',
      zone: 'network',
      stage: 'R3_UAT_READY',
      purpose: 'Main operating cockpit for plan, execute and closing mode.',
      defaultVisible: true,
    },
    {
      itemId: 'logistics.network.uat',
      label: 'R3 UAT Pack',
      route: '/network/logistics-capability?panel=uat',
      zone: 'network',
      stage: 'R3_UAT_READY',
      purpose: 'UAT scenarios, rehearsal script and production locks.',
      defaultVisible: true,
    },
    {
      itemId: 'logistics.network.pilot-prep',
      label: 'R4 Pilot Prep',
      route: '/network/logistics-capability?panel=r4-pilot-prep',
      zone: 'network',
      stage: 'R4_PILOT_PREP_READY',
      purpose: 'Pilot checklist before real tenant data and approval-only side effects.',
      defaultVisible: true,
    },
    {
      itemId: 'logistics.network.fm-workbench',
      label: 'FM Workbench Link',
      route: '/fm/shipments?capability=logistics-network',
      zone: 'fm',
      stage: 'R4_PILOT_PREP_READY',
      purpose: 'Open relevant shipment, nomination, DG, transhipment and co-loading work from FM.',
      defaultVisible: false,
    },
    {
      itemId: 'logistics.network.ai-control',
      label: 'AI Control Tower Link',
      route: '/ai/inbox?capability=logistics-network',
      zone: 'ai',
      stage: 'R4_PILOT_PREP_READY',
      purpose: 'Review email drafts, guard decisions and approval requests from AI module.',
      defaultVisible: false,
    },
    {
      itemId: 'logistics.network.settlement',
      label: 'Settlement Control Link',
      route: '/finance/settlement?capability=logistics-network',
      zone: 'finance',
      stage: 'R5_COMMERCIAL_CAPABILITY',
      purpose: 'Review agent settlement, debit note and profit split drafts.',
      defaultVisible: false,
    },
    {
      itemId: 'logistics.network.partner-graph',
      label: 'Partner Graph',
      route: '/network/logistics-capability?panel=partner-graph',
      zone: 'network',
      stage: 'R6_NETWORK_ECOSYSTEM',
      purpose: 'Future partner/lane network graph after privacy and settlement governance is live.',
      defaultVisible: false,
    },
  ],
  activationRules: [
    'Default entry sits under existing Network menu.',
    'FM, AI and Finance entries are contextual deep-links enabled by tenant capability profile.',
    'R5/R6 entries stay hidden until production gates explicitly pass.',
  ],
  nonClaims: [
    'This menu set does not create a separate product silo.',
    'Hidden future menu items do not imply R5/R6 production readiness.',
    'Tenant navigation config must explicitly enable production visibility.',
  ],
};

const demoCapability: CapabilityPayload = {
  capabilityId: 'boox.logistics-network.freight-pattern',
  status: 'R3_UAT_READY',
  productizationPack: {} as LogisticsProductizationPack,
  readiness: demoReadiness,
  uatPack: demoUatPack,
  roadmapPack: demoRoadmapPack,
  pilotPrepPack: demoPilotPrepPack,
  menuSet: demoMenuSet,
};

const demoCases: CaseListItem[] = [
  {
    caseId: 'FN-R2-NOM-DG-001',
    mode: 'plan',
    patterns: ['nomination', 'dg', 'co_loading', 'docs'],
    route: { origin: 'Port Klang', destination: 'Shanghai', mode: 'sea' },
    riskCount: 2,
    approvalRequired: true,
  },
  {
    caseId: 'FN-R2-TRANS-001',
    mode: 'execute',
    patterns: ['transhipment', 'carrier', 'docs'],
    route: { origin: 'Jakarta', destination: 'Dubai', mode: 'multimodal' },
    riskCount: 1,
    approvalRequired: true,
  },
];

function buildDemoCockpit(item: CaseListItem): CockpitPayload {
  return {
    projection: {
      tenantId: 'demo-tenant',
      caseId: item.caseId,
      mode: item.mode,
      patterns: item.patterns,
      route: item.route,
      approvalRequired: item.approvalRequired,
      partyGraph: [],
      cargo: {},
      missingFields: [],
      riskFlags: [],
      readiness: { quote: {}, document: {}, cost: {}, dg: {} },
      playbookLenses: [],
      attentionCards: [],
      nextActions: [],
    } as unknown as LogisticsCaseProjection,
    cockpitView: {
      caseId: item.caseId,
      mode: item.mode,
      header: {
        title: item.caseId,
        routeLabel: routeLabel(item),
        riskCount: item.riskCount,
        approvalRequired: item.approvalRequired,
      },
      topAttentionCards: [
        {
          cardId: `${item.caseId}.dg-doc`,
          title: item.patterns.includes('dg') ? 'Request SDS/MSDS from shipper' : 'Confirm transhipment cut-off',
          severity: item.patterns.includes('dg') ? 'critical' : 'warning',
          mode: item.mode,
          lensKind: item.patterns.includes('dg') ? 'dg' : 'transhipment',
          ownerRole: 'internal_ops',
          actionType: 'approve',
          approvalRequired: true,
          evidence: [],
        },
      ],
      mountedLensKinds: item.patterns,
      doneSummary: ['Customer lane identified', 'Partner handoff mapped', 'Draft cost comparison prepared'],
      pendingSummary: ['Missing external confirmation', 'Approval required before side effect'],
      tomorrowPrep: ['Review approval pack', 'Check cut-off and document readiness'],
    } as LogisticsCaseCockpitView,
    hardeningReview: {
      policyVersion: 'r2.demo',
      guardDecisions: [
        {
          actionId: `${item.caseId}.external-send`,
          actionType: 'external_send',
          decision: 'blocked',
          sideEffectCategory: 'external',
          approvalRequired: true,
          auditRequired: true,
          reason: 'External side effects disabled in R2 demo.',
          fallbackAction: 'Create approval draft and keep unsent.',
        },
        {
          actionId: `${item.caseId}.ledger-post`,
          actionType: 'post_ledger',
          decision: 'blocked',
          sideEffectCategory: 'financial',
          approvalRequired: true,
          auditRequired: true,
          reason: 'Financial posting must go through AC approval.',
          fallbackAction: 'Route to finance approval.',
        },
      ],
      blockedActionCount: 2,
      approvalRequiredCount: 0,
      checkedAt: '2026-05-15T00:00:00.000Z',
    } as unknown as LogisticsR2HardeningReview,
    readiness: demoReadiness,
    uatPack: demoUatPack,
    roadmapPack: demoRoadmapPack,
    pilotPrepPack: demoPilotPrepPack,
    menuSet: demoMenuSet,
  };
}

function routeLabel(item: CaseListItem | LogisticsCaseProjection) {
  const route = item.route;
  const origin = route.origin ?? route.pol ?? 'Origin pending';
  const destination = route.destination ?? route.pod ?? 'Destination pending';
  return `${origin} -> ${destination}`;
}

function statusTone(status: string | undefined) {
  if (!status) return 'border-slate-200 bg-slate-50 text-slate-700';
  if (status.includes('READY') || status === 'pass') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status.includes('BLOCKED') || status === 'fail') return 'border-rose-200 bg-rose-50 text-rose-800';
  return 'border-amber-200 bg-amber-50 text-amber-800';
}

function Metric({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Gauge;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium uppercase text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="mt-3 text-2xl font-semibold text-foreground">{value}</div>
    </div>
  );
}

export default function LogisticsNetworkCapabilityPage() {
  const demoMode = typeof window !== 'undefined' && window.location.pathname.startsWith('/demo/logistics-capability');
  const [capability, setCapability] = useState<CapabilityPayload | null>(null);
  const [cases, setCases] = useState<CaseListItem[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [cockpit, setCockpit] = useState<CockpitPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [caseLoading, setCaseLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCase = useCallback(async (caseId: string) => {
    if (!caseId) return;
    setCaseLoading(true);
    try {
      if (demoMode) {
        const item = demoCases.find((candidate) => candidate.caseId === caseId) ?? demoCases[0];
        if (!item) return;
        setCockpit(buildDemoCockpit(item));
        setSelectedCaseId(caseId);
        return;
      }
      const detail = await api.get<CockpitPayload>(`/logistics-network/cases/${encodeURIComponent(caseId)}/cockpit`);
      setCockpit(detail);
      setSelectedCaseId(caseId);
    } finally {
      setCaseLoading(false);
    }
  }, [demoMode]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (demoMode) {
        setCapability(demoCapability);
        setCases(demoCases);
        const nextCaseId = selectedCaseId || demoCases[0]?.caseId || '';
        if (nextCaseId) await loadCase(nextCaseId);
        return;
      }
      const [capabilityPayload, casesPayload] = await Promise.all([
        api.get<CapabilityPayload>('/logistics-network/capability'),
        api.get<CasesPayload>('/logistics-network/cases'),
      ]);
      setCapability(capabilityPayload);
      setCases(casesPayload.items);
      const nextCaseId = selectedCaseId || casesPayload.items[0]?.caseId || '';
      if (nextCaseId) await loadCase(nextCaseId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logistics capability');
    } finally {
      setLoading(false);
    }
  }, [demoMode, loadCase, selectedCaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const blockedActions = useMemo(
    () => cockpit?.hardeningReview?.guardDecisions.filter((item) => item.decision === 'blocked') ?? [],
    [cockpit],
  );
  const approvalActions = useMemo(
    () => cockpit?.hardeningReview?.guardDecisions.filter((item) => item.decision === 'approval_required') ?? [],
    [cockpit],
  );
  const readinessGates = capability?.readiness?.gates ?? [];
  const cockpitView = cockpit?.cockpitView;
  const pendingSummary = cockpitView?.pendingSummary ?? [];
  const doneSummary = cockpitView?.doneSummary ?? [];
  const topAttentionCards = cockpitView?.topAttentionCards ?? [];
  const mountedLensKinds = cockpitView?.mountedLensKinds ?? [];
  const tomorrowPrep = cockpitView?.tomorrowPrep ?? [];
  const uatPack = capability?.uatPack ?? cockpit?.uatPack;
  const uatScenarios = uatPack?.scenarios ?? [];
  const roadmapPack = capability?.roadmapPack ?? cockpit?.roadmapPack;
  const roadmapStages = roadmapPack?.stages ?? [];
  const pilotPrepPack = capability?.pilotPrepPack ?? cockpit?.pilotPrepPack;
  const pilotWorkstreams = pilotPrepPack?.workstreams ?? [];
  const menuSet = capability?.menuSet ?? cockpit?.menuSet;
  const menuItems = menuSet?.items ?? [];

  if (loading && !capability) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-80" />
        <div className="grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28" />)}
        </div>
        <Skeleton className="h-[480px]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-5 p-5 lg:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-normal text-foreground">Logistics Network Capability</h1>
              {capability && (
                <Badge variant="outline" className={cn('border', statusTone(capability.status))}>
                  {capability.status}
                </Badge>
              )}
            </div>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              One Case Cockpit for nomination, DG, transhipment, co-loading, charges and settlement, with R2 guardrails.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-4">
          <Metric label="Cases" value={String(cases.length)} icon={ClipboardList} />
          <Metric label="Blocked Actions" value={String(blockedActions.length)} icon={ShieldCheck} />
          <Metric label="Approval Drafts" value={String(approvalActions.length)} icon={Bot} />
          <Metric label="Menu Items" value={String(menuItems.length)} icon={Gauge} />
        </div>

        <div className="grid gap-5 xl:grid-cols-[380px_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Network className="h-4 w-4" />
                Today Board
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {cases.map((item) => (
                <button
                  key={item.caseId}
                  type="button"
                  onClick={() => void loadCase(item.caseId)}
                  className={cn(
                    'w-full rounded-lg border p-3 text-left transition hover:border-primary/60 hover:bg-primary/5',
                    selectedCaseId === item.caseId ? 'border-primary bg-primary/5' : 'border-border bg-background',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground">{item.caseId}</span>
                    <Badge variant="secondary">{item.mode}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{routeLabel(item)}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {item.patterns.slice(0, 4).map((pattern) => (
                      <Badge key={pattern} variant="outline" className="text-[11px]">{pattern}</Badge>
                    ))}
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3 text-base">
                  <span className="flex items-center gap-2">
                    <Ship className="h-4 w-4" />
                    Case Cockpit
                  </span>
                  {cockpitView && <Badge variant="outline">{cockpitView.mode}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {caseLoading || !cockpit?.projection || !cockpitView ? (
                  <Skeleton className="h-72" />
                ) : (
                  <div className="space-y-5">
                    <div className="grid gap-3 md:grid-cols-3">
                      <div className="rounded-lg border border-border bg-background p-4">
                        <div className="text-xs text-muted-foreground">Where are we</div>
                        <div className="mt-2 text-sm font-medium">{routeLabel(cockpit.projection)}</div>
                      </div>
                      <div className="rounded-lg border border-border bg-background p-4">
                        <div className="text-xs text-muted-foreground">Need to do</div>
                        <div className="mt-2 text-sm font-medium">{pendingSummary.length} pending</div>
                      </div>
                      <div className="rounded-lg border border-border bg-background p-4">
                        <div className="text-xs text-muted-foreground">Done</div>
                        <div className="mt-2 text-sm font-medium">{doneSummary.length} completed</div>
                      </div>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-2">
                      <section className="rounded-lg border border-border bg-background p-4">
                        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          Attention
                        </div>
                        <div className="space-y-2">
                          {topAttentionCards.map((card) => (
                            <div key={card.cardId} className="rounded-md border border-border p-3">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium">{card.title}</span>
                                <Badge variant={card.severity === 'critical' ? 'destructive' : 'outline'}>{card.severity}</Badge>
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">{card.actionType}</div>
                            </div>
                          ))}
                        </div>
                      </section>

                      <section className="rounded-lg border border-border bg-background p-4">
                        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          Playbook Lenses
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {mountedLensKinds.map((lens) => (
                            <Badge key={lens} variant="secondary">{lens}</Badge>
                          ))}
                        </div>
                        <div className="mt-4 space-y-1 text-sm text-muted-foreground">
                          {tomorrowPrep.map((item) => <div key={item}>{item}</div>)}
                        </div>
                      </section>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-5 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ShieldCheck className="h-4 w-4" />
                    R2 Guardrails
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {cockpit?.hardeningReview?.guardDecisions.map((decision) => (
                    <div key={decision.actionId} className="rounded-lg border border-border bg-background p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium">{decision.actionType}</span>
                        <Badge variant="outline" className={cn('border', statusTone(decision.decision))}>
                          {decision.decision}
                        </Badge>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{decision.fallbackAction}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CheckCircle2 className="h-4 w-4" />
                    R3 UAT Readiness
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {readinessGates.map((gate) => (
                    <div key={gate.gateId} className="rounded-lg border border-border bg-background p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{gate.label}</span>
                        <Badge variant="outline" className={cn('border', statusTone(gate.status))}>
                          {gate.status}
                        </Badge>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{gate.evidence}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3 text-base">
                  <span className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    R3 UAT Pack
                  </span>
                  {uatPack && (
                    <Badge variant="outline" className={cn('border', statusTone(uatPack.status))}>
                      {uatPack.status}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                {uatScenarios.map((scenario) => (
                  <div key={scenario.scenarioId} className="rounded-lg border border-border bg-background p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{scenario.label}</span>
                      <Badge variant="outline" className={cn('border', statusTone(scenario.status))}>
                        {scenario.status}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{scenario.role} / {scenario.mode}</div>
                    <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                      {scenario.expectedEvidence.slice(0, 3).map((item) => <div key={item}>{item}</div>)}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3 text-base">
                  <span className="flex items-center gap-2">
                    <Network className="h-4 w-4" />
                    Capability Menu Set
                  </span>
                  {menuSet && <Badge variant="outline">{menuSet.strategy}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {menuSet && (
                  <p className="text-sm text-muted-foreground">{menuSet.recommendation}</p>
                )}
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  {menuItems.map((item) => (
                    <div key={item.itemId} className="rounded-lg border border-border bg-background p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{item.label}</span>
                        <Badge variant={item.defaultVisible ? 'secondary' : 'outline'}>
                          {item.defaultVisible ? 'visible' : 'conditional'}
                        </Badge>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{item.zone} / {item.stage}</div>
                      <div className="mt-3 text-xs text-muted-foreground">{item.purpose}</div>
                    </div>
                  ))}
                </div>
                {menuSet && (
                  <div className="grid gap-3 md:grid-cols-2">
                    <section className="rounded-lg border border-border bg-background p-3">
                      <div className="text-xs font-medium text-foreground">Activation Rules</div>
                      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {menuSet.activationRules.map((item) => <div key={item}>{item}</div>)}
                      </div>
                    </section>
                    <section className="rounded-lg border border-border bg-background p-3">
                      <div className="text-xs font-medium text-foreground">Non Claims</div>
                      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {menuSet.nonClaims.map((item) => <div key={item}>{item}</div>)}
                      </div>
                    </section>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3 text-base">
                  <span className="flex items-center gap-2">
                    <Network className="h-4 w-4" />
                    R4-R6 PRD Roadmap
                  </span>
                  {roadmapPack && (
                    <Badge variant="outline" className={cn('border', statusTone(roadmapPack.status))}>
                      {roadmapPack.status}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                {roadmapStages.map((stage) => (
                  <div key={stage.stageId} className="rounded-lg border border-border bg-background p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{stage.label}</span>
                      <Badge variant="outline" className={cn('border', statusTone(stage.status))}>
                        {stage.status}
                      </Badge>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">{stage.objective}</div>
                    <div className="mt-3 text-xs font-medium text-foreground">Boundary</div>
                    <div className="mt-1 text-xs text-muted-foreground">{stage.productionBoundary}</div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3 text-base">
                  <span className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" />
                    R4 Pilot Prep
                  </span>
                  {pilotPrepPack && (
                    <Badge variant="outline" className={cn('border', statusTone(pilotPrepPack.status))}>
                      {pilotPrepPack.status}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-5">
                {pilotWorkstreams.map((workstream) => (
                  <div key={workstream.workstreamId} className="rounded-lg border border-border bg-background p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{workstream.label}</span>
                      <Badge variant="outline" className={cn('border', statusTone(workstream.status))}>
                        {workstream.status}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{workstream.owner}</div>
                    <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                      {workstream.checklist.slice(0, 3).map((item) => <div key={item}>{item}</div>)}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
